import React, { useState, useRef, useEffect } from "react";
import "./Contact.css";
import { useGardenPlan } from "../context/GardenPlanContext";

function encode(data) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
    .join("&");
}

function useReveal(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

const REASONS = [
  { icon: "◈", text: "Zuverlässig & erfahren seit über 15 Jahren" },
  { icon: "◉", text: "Faire Preise & transparente Angebote" },
  { icon: "✦", text: "Kundennah, flexibel & termingerecht" },
  { icon: "❋", text: "Individuelle Planung für jedes Projekt" },
];

const Contact = () => {
  const rootRef    = useRef(null);
  const visible    = useReveal(rootRef);
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState("");
  const [sent,    setSent]    = useState(false);
  const [focused, setFocused] = useState("");
  const [withSketch, setWithSketch] = useState(true);
  const messageRef = useRef(null);

  // Entwurf aus dem Traumgarten-Planer
  const garden     = useGardenPlan();
  const gardenPlan = garden?.plan;
  const gardenSummary = gardenPlan?.attached ? gardenPlan.summary : null;
  const gardenLines = gardenSummary?.lines || [];
  const hasGarden  = gardenLines.length > 0;

  useEffect(() => {
    const field = messageRef.current;
    if (hasGarden && field && !field.value.trim()) {
      field.value = "Guten Tag, ich möchte meinen Traumgarten-Entwurf gerne mit Ihnen besprechen.";
    }
  }, [hasGarden]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    const form = event.currentTarget;
    const fd = new FormData(form);
    const formData = {
      "form-name": "contact",
      name:       (fd.get("name")    || "").trim(),
      email:      (fd.get("email")   || "").trim(),
      phone:      (fd.get("phone")   || "").trim(),
      message:    (fd.get("message") || "").trim(),
      "bot-field":(fd.get("bot-field") || "").trim(),
      consent:    fd.get("consent") ? "on" : "",
      garden_plan: hasGarden ? gardenSummary.text : "",
    };

    if (!formData.name || !formData.email || !formData.message) {
      setError("Bitte Name, E-Mail und Nachricht ausfüllen.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Ungültige E-Mail-Adresse.");
      return;
    }
    if (formData["bot-field"]) return;

    try {
      setSending(true);
      // Planskizze als Bild – nur wenn ein Entwurf beiliegt; sonst bleibt der bisherige Versand unverändert
      let sketch = null;
      if (hasGarden && withSketch && garden?.getSketch) {
        try { sketch = await garden.getSketch(); } catch { sketch = null; }
      }
      if (sketch) {
        const body = new FormData();
        Object.entries(formData).forEach(([k, v]) => body.append(k, v));
        body.append("garden_sketch", new File([sketch], "traumgarten-entwurf.jpg", { type: "image/jpeg" }));
        await fetch("/", { method: "POST", body });
      } else {
        await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: encode(formData),
        });
      }
      setSent(true);
      garden?.detach();
      form.reset();
    } catch {
      setError("Senden fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section id="contact" className="ct-root">
        <div className="ct-success">
          <span className="ct-success-icon">✦</span>
          <h2 className="ct-success-title">Vielen Dank!</h2>
          <p className="ct-success-sub">Wir melden uns schnellstmöglich bei Ihnen.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="ct-root" ref={rootRef}>

      {/* Background layers */}
      <div className="ct-bg-glow ct-bg-glow--1" aria-hidden />
      <div className="ct-bg-glow ct-bg-glow--2" aria-hidden />

      {/* Header */}
      <div className={`ct-header${visible ? " ct-visible" : ""}`}>
        <span className="ct-eyebrow">Kontakt</span>
        <h2 className="ct-title">
          Lassen Sie uns<br /><em>gemeinsam planen</em>
        </h2>
        <p className="ct-subtitle">
          Füllen Sie das Formular aus — wir melden uns innerhalb von 24 Stunden.
        </p>
      </div>

      {/* Two-column layout */}
      <div className={`ct-body${visible ? " ct-visible" : ""}`}>

        {/* Left: Info panel */}
        <aside className="ct-info">
          <h3 className="ct-info-title">Warum EG-Bau?</h3>
          <ul className="ct-reasons">
            {REASONS.map((r, i) => (
              <li key={i} className="ct-reason" style={{ transitionDelay: `${i * 80 + 200}ms` }}>
                <span className="ct-reason-icon">{r.icon}</span>
                <span>{r.text}</span>
              </li>
            ))}
          </ul>

          <div className="ct-contact-details">
            <div className="ct-detail">
              <span className="ct-detail-label">Telefon</span>
              <a href="tel:+4915155629922" className="ct-detail-value">
                +49 151 5562 9922
              </a>
            </div>
            <div className="ct-detail-divider" />
            <div className="ct-detail">
              <span className="ct-detail-label">E-Mail</span>
              <a href="mailto:info@gala-egbau.de" className="ct-detail-value">
                info@gala-egbau.de
              </a>
            </div>
          </div>

          {/* Decorative number */}
          <span className="ct-deco-num" aria-hidden>EG</span>
        </aside>

        {/* Right: Form */}
        <form
          className="ct-form"
          name="contact"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          data-netlify-recaptcha="true"
          acceptCharset="UTF-8"
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="form-name" value="contact" />
          <p style={{ display: "none" }}>
            <label>Don't fill: <input name="bot-field" /></label>
          </p>

          {/* Row 1 */}
          <div className="ct-row">
            <div className={`ct-field${focused === "name" ? " ct-field--focused" : ""}`}>
              <label className="ct-label" htmlFor="ct-name">Name *</label>
              <input
                id="ct-name"
                type="text"
                name="name"
                placeholder="Vor- und Nachname"
                autoComplete="name"
                required
                onFocus={() => setFocused("name")}
                onBlur={() => setFocused("")}
              />
            </div>
            <div className={`ct-field${focused === "email" ? " ct-field--focused" : ""}`}>
              <label className="ct-label" htmlFor="ct-email">E-Mail *</label>
              <input
                id="ct-email"
                type="email"
                name="email"
                placeholder="ihre@email.de"
                autoComplete="email"
                required
                onFocus={() => setFocused("email")}
                onBlur={() => setFocused("")}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="ct-row">
            <div className={`ct-field${focused === "phone" ? " ct-field--focused" : ""}`}>
              <label className="ct-label" htmlFor="ct-phone">Telefon <span className="ct-optional">(optional)</span></label>
              <input
                id="ct-phone"
                type="tel"
                name="phone"
                placeholder="+49 …"
                autoComplete="tel"
                onFocus={() => setFocused("phone")}
                onBlur={() => setFocused("")}
              />
            </div>
          </div>

          {/* Traumgarten-Entwurf */}
          {hasGarden && (
            <div className="ct-garden">
              <div className="ct-garden-head">
                <span className="ct-label">Ihr Traumgarten-Entwurf</span>
                <button type="button" className="ct-garden-remove" onClick={garden.detach}>
                  Entfernen
                </button>
              </div>
              <ul className="ct-garden-list">
                {gardenLines.map((l) => (
                  <li key={l.id}>
                    <span className="ct-garden-num">{l.num}</span>
                    {l.title}
                    <em>{l.variant}</em>
                  </li>
                ))}
              </ul>
              <label className="ct-garden-sketch">
                <input type="checkbox" checked={withSketch} onChange={(e) => setWithSketch(e.target.checked)} />
                <span>Planskizze als Bild mitsenden</span>
              </label>
              <p className="ct-garden-meta">
                Grundstück: {gardenSummary.size} ·{" "}
                <a href="#traumgarten">Entwurf bearbeiten</a>
              </p>
            </div>
          )}

          {/* Message */}
          <div className={`ct-field ct-field--full${focused === "message" ? " ct-field--focused" : ""}`}>
            <label className="ct-label" htmlFor="ct-message">Ihre Anfrage *</label>
            <textarea
              id="ct-message"
              ref={messageRef}
              name="message"
              placeholder="Beschreiben Sie Ihr Projekt…"
              required
              maxLength={3000}
              onFocus={() => setFocused("message")}
              onBlur={() => setFocused("")}
            />
          </div>

          {/* Consent */}
          <label className="ct-consent">
            <input type="checkbox" name="consent" required />
            <span>
              Ich stimme der Verarbeitung meiner Angaben gemäß{" "}
              <a href="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a> zu.
            </span>
          </label>

          {error && <p className="ct-error">{error}</p>}

          <div data-netlify-recaptcha="true" className="ct-recaptcha" />

          <button type="submit" className="ct-submit" disabled={sending}>
            <span>{sending ? "Senden…" : "Anfrage absenden"}</span>
            {!sending && (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;